<template>
  <sf-modal v-if="modalOpen" size="xs" @close="close">
    <template #title>
      {{ I18n.t('permanent_document.modal_select.modal_title') }}
    </template>

    <template #body>
      <div v-if="!permanentCategory">
        <div data-behaviour="select-document-file-input-placeholder">
          <h5>
            {{ I18n.t('permanent_document.modal_select.modal_new_file') }}
          </h5>
        </div>

        <direct-upload-dropzone :on-file-uploaded="handleFileUploaded">
          <div
            class="
              sf-layer-border
              sf-layer-rounded
              sf-bg-snow-light
              sf-layer-button
              sf-pad-2
              sf-below-3
            "
          >
            <div class="sf-level">
              <span
                class="
                  sf-font-xl sf-c-snow-darker
                  icon icon-cloud-upload
                  sf-pad-1
                "
              />
              <div
                v-dompurify-html="
                  I18n.t('components.document.dropzone_html', {
                    href: `<a>${I18n.t('components.document.browse')}</a>`,
                  })
                "
                style="max-width: 200px"
              />
            </div>
          </div>
        </direct-upload-dropzone>

        <h5>
          {{ I18n.t('permanent_document.modal_select.modal_from_documents') }}
        </h5>
      </div>

      <document-breadcrumbs
        v-else
        :parent-category="parentCategory"
        :permanent-category="permanentCategory"
        :navigate-function="navigateTo"
        :navigate-params="{
          permanent_category_id: parentCategory ? parentCategory.id : null,
        }"
      />

      <div
        v-if="permanentEntries.length > 0 && !loading"
        class="sf-tab sf-tab-icons"
      >
        <a
          v-for="entry in permanentEntries"
          :key="entry.id"
          class="sf-a"
          @click="selectEntry(entry)"
        >
          <div class="sf-tab-item sf-block">
            <div class="sf-tab-item-icon">
              <span :class="entry.icon" />
            </div>
            <div>{{ entry.title }}</div>
          </div>
        </a>
      </div>
      <div v-else-if="loading" class="sf-level sf-level-around sf-pad-3">
        <i class="icon-spinner icon-spin export-spinner" />
      </div>
      <div v-else class="sf-level sf-level-around sf-pad-3">
        <p class="muted">
          {{ I18n.t('permanent_document.modal_select.empty_folder') }}
        </p>
      </div>
    </template>
  </sf-modal>
</template>

<script>
import { SfModal } from '@app/shared/vue/components/sf-modal'
import documentBreadcrumbs from '@app/application/document/sections/document-breadcrumbs'
import directUploadDropzone from '@app/shared/vue/components/direct-upload/direct-upload-dropzone'
import SFetch from '@app/utils/sfetch'

export default {
  name: 'ExportSelectFromDocumentsModal',
  components: { SfModal, documentBreadcrumbs, directUploadDropzone },
  data() {
    return {
      modalOpen: false,
      permanentCategory: null,
      permanentEntries: [],
      exportElementId: null,
      loading: true,
    }
  },

  computed: {
    parentCategory() {
      return this.permanentCategory && this.permanentCategory.parentCategory
    },
  },

  methods: {
    openModal(params) {
      this.modalOpen = true
      this.permanentCategory = null
      this.permanentEntries = {}
      this.loading = true
      this.exportElementId = params.exportElementId

      this.navigateTo()
    },
    close() {
      this.modalOpen = false
    },
    setData(params) {
      this.permanentCategory = params.permanentCategory
      this.permanentEntries = params.permanentEntries
      this.loading = false
    },
    navigateTo(params = {}) {
      this.loading = true

      const url = Silverfin.Routes.select_from_documents_permanent_entries_path(
        {
          firm_id: Silverfin.currentFirmId,
          company_id: Silverfin.currentCompanyId,
          only_previews_and_pdfs: true,
          ...params,
          _options: true,
        }
      )

      SFetch.json(url, { method: 'GET' }).then((response) =>
        this.setData(response)
      )
    },
    selectDocument(entry) {
      const formElement = document.getElementById(this.exportElementId)
      if (formElement) {
        formElement.querySelector(
          '[name="export[template_hash][content][][data][permanent_document_id]"]'
        ).value = entry.id
        this.handleFileChange(formElement, entry.slugPath, entry.documentUrl)

        //Document name should remain if present
        if (
          !formElement
            .closest('li')
            .classList.contains('element-from-placeholder')
        ) {
          formElement.querySelector(
            '[name="export[template_hash][content][][data][title]"]'
          ).value = entry.title
        }
      }

      this.close()
    },
    selectEntry(entry) {
      if (entry.category) {
        return this.navigateTo({ permanent_category_id: entry.id })
      } else {
        return this.selectDocument(entry)
      }
    },
    handleFileChange(formElement, filepath, url) {
      const documentFilePathSection = formElement.querySelector(
        '[data-behaviour="document-file-path-section"]'
      )
      documentFilePathSection.querySelector(
        '[data-behaviour="export-select-from-documents"]'
      ).hidden = true
      const documentFilePath = documentFilePathSection.querySelector(
        '[data-behaviour="document-file-path"]'
      )
      documentFilePath.innerHTML = filepath
      documentFilePath.hidden = false
      documentFilePath.href = url

      if (url === '') {
        documentFilePath.classList.add('sf-disabled')
      } else {
        documentFilePath.classList.remove('sf-disabled')
      }
    },
    handleFileUploaded(file) {
      const formElement = document.getElementById(this.exportElementId)

      if (formElement) {
        formElement.querySelector(
          '[data-behaviour~="s3-doc-directUploadPath"]'
        ).value = file.blob.signed_id
        formElement.querySelector('[data-behaviour~="s3-doc-fileName"]').value =
          file.blob.filename
        formElement.querySelector(
          '[data-behaviour~="s3-doc-contentType"]'
        ).value = file.blob.content_type
        formElement.querySelector('[data-behaviour~="s3-doc-fileSize"]').value =
          file.blob.byte_size

        this.handleFileChange(formElement, file.blob.filename, '')

        //Document name should remain if present if it comes from a placeholder
        if (
          !formElement
            .closest('li')
            .classList.contains('element-from-placeholder')
        ) {
          formElement.querySelector(
            '[name="export[template_hash][content][][data][title]"]'
          ).value = file.blob.filename
        }
      }

      this.close()
    },
  },
}
</script>
